import type { MutableRefObject, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';

import type { TypeTabs } from '@/ui';
import { Box, Tabs } from '@/ui';
import { getSxClasses } from './footer-bar-style';
import { ResizeFooterPanel } from '@/core/components/footer-bar/hooks/resize-footer-panel';
import { useAppFullscreenActive, useAppHeight, useAppShellContainer } from '@/core/stores/store-interface-and-intial-values/app-state';
import { useDetailsLayerDataArrayBatch } from '@/core/stores/store-interface-and-intial-values/feature-info-state';
import {
  useUIActiveFooterBarTab,
  useUIFooterPanelResizeValue,
  useUIStoreActions,
  useUIActiveTrapGeoView,
  useUIHiddenTabs,
} from '@/core/stores/store-interface-and-intial-values/ui-state';
import type { FooterBarApi, FooterTabCreatedEvent, FooterTabRemovedEvent } from '@/core/components';
import { DEFAULT_FOOTER_TABS_ORDER } from '@/api/types/map-schema-types';
import { useGeoViewConfig, useGeoViewMapId } from '@/core/stores/geoview-store';

// default tabs icon and class
import { LegendIcon, InfoIcon, LayersIcon, StorageIcon, QuestionMarkIcon } from '@/ui/icons';
import { UseHtmlToReact } from '@/core/components/common/hooks/use-html-to-react';
import { Legend } from '@/core/components/legend/legend';
import { LayersPanel } from '@/core/components/layers/layers-panel';
import { DetailsPanel } from '@/core/components/details/details-panel';
import { Datapanel } from '@/core/components/data-table/data-panel';
import { logger } from '@/core/utils/logger';
import { Guide } from '@/core/components/guide/guide';
import { MapEventProcessor } from '@/api/event-processors/event-processor-children/map-event-processor';
import { FooterPlugin } from '@/api/plugin/footer-plugin';
import { CONTAINER_TYPE } from '@/core/utils/constant';
import { camelCase, delay, scrollIfNotVisible } from '@/core/utils/utilities';

/** Tab definition with icon, content, and optional label. */
interface Tab {
  /** The tab icon element. */
  icon: ReactNode;
  /** The tab content element. */
  content: ReactNode;
  /** Optional tab label. */
  label?: string;
}

/** Props for the FooterBar component. */
type FooterBarProps = {
  /** The footer bar API instance. */
  api: FooterBarApi;
};

/**
 * Creates the FooterBar component to display a list of tabs and their content.
 *
 * @param props - The footer bar props
 * @returns The FooterBar tabs component, or null if no tabs
 */
export function FooterBar(props: FooterBarProps): JSX.Element | null {
  // Log
  logger.logTraceRender('components/footer-bar/footer-bar');

  // Set props
  const { api: footerBarApi } = props;

  // Hooks
  const isMapFullScreen = useAppFullscreenActive();
  const footerPanelResizeValue = useUIFooterPanelResizeValue();
  const theme = useTheme();
  const sxClasses = useMemo(() => getSxClasses(theme), [theme]);

  // State & ref
  const tabsContainerRef = useRef<HTMLDivElement>();

  // Store
  const mapId = useGeoViewMapId();
  const arrayOfLayerDataBatch = useDetailsLayerDataArrayBatch();
  const activeFooterBarTab = useUIActiveFooterBarTab();
  const activeTrapGeoView = useUIActiveTrapGeoView();
  const shellContainer = useAppShellContainer();
  const { setActiveFooterBarTab, enableFocusTrap, disableFocusTrap, setFooterBarIsOpen } = useUIStoreActions();
  const backupAppHeight: number = useAppHeight();
  const appHeight = document.getElementById(mapId)?.getAttribute('data-footer-height') ?? `${backupAppHeight}px`;
  const hiddenTabs: string[] = useUIHiddenTabs();

  // get store config for footer bar tabs to add (similar logic as in app-bar)
  const footerBarTabsConfig = useGeoViewConfig()?.footerBar;

  /**
   * Builds the footer bar tab keys from configuration.
   */
  const memoFooterBarTabKeys = useMemo(() => {
    // Log
    logger.logTraceUseMemo('FOOTER-BAR - memoFooterBarTabKeys', footerBarTabsConfig?.tabs?.core);

    const coreTabs = (footerBarTabsConfig?.tabs?.core ?? []).reduce(
      (acc, curr) => {
        // eslint-disable-next-line no-param-reassign
        acc[curr] = {} as Tab;
        return acc;
      },
      {} as Record<string, Tab>
    );

    // Add custom tabs
    const customTabs = (footerBarTabsConfig?.tabs?.custom ?? []).reduce(
      (acc, curr) => {
        // eslint-disable-next-line no-param-reassign
        acc[curr.id] = {} as Tab;
        return acc;
      },
      {} as Record<string, Tab>
    );

    return { ...coreTabs, ...customTabs };
  }, [footerBarTabsConfig?.tabs]);

  // List of Footer Tabs created from config file.
  const [tabsList, setTabsList] = useState<Record<string, Tab>>(memoFooterBarTabKeys);

  /**
   * Creates custom tabs from configuration.
   */
  useEffect(() => {
    if (footerBarTabsConfig?.tabs?.custom) {
      footerBarTabsConfig.tabs.custom.forEach((customTab) => {
        const newTab = {
          [customTab.id]: {
            icon: <InfoIcon />, // TODO: Make it configurable if needed
            label: customTab.label,
            content: <UseHtmlToReact htmlContent={customTab.contentHTML ?? ''} />,
          },
        } as Record<string, Tab>;

        // Add the custom tab to the tabs list
        setTabsList((prevState: Record<string, Tab>) => {
          return { ...prevState, ...newTab };
        });
      });
    }
  }, [footerBarTabsConfig]);

  /**
   * Builds the panel definitions for each core tab.
   */
  const memoTabs = useMemo(() => {
    // Log
    logger.logTraceUseMemo('FOOTER-BAR - memoTabs');

    return {
      legend: { icon: <LegendIcon />, content: <Legend containerType={CONTAINER_TYPE.FOOTER_BAR} /> },
      layers: { icon: <LayersIcon />, content: <LayersPanel containerType={CONTAINER_TYPE.FOOTER_BAR} /> },
      details: { icon: <InfoIcon />, content: <DetailsPanel containerType={CONTAINER_TYPE.FOOTER_BAR} /> },
      'data-table': { icon: <StorageIcon />, content: <Datapanel containerType={CONTAINER_TYPE.FOOTER_BAR} /> },
      guide: { icon: <QuestionMarkIcon />, content: <Guide containerType={CONTAINER_TYPE.FOOTER_BAR} /> },
    } as Record<string, Tab>;
  }, []);

  /**
   * Maps the panels with footer bar tab keys in display order.
   */
  const memoFooterBarTabs = useMemo(() => {
    // Log
    logger.logTraceUseMemo('FOOTER-BAR - memoFooterBarTabs', tabsList, memoTabs);

    // Early return if no footer tab provided
    if (!tabsList || Object.keys(tabsList).length === 0) return [];

    // Set the allTabs (union of all footer tabs) and get the needed keys
    const allTabs = { ...tabsList, ...memoTabs };
    const tabsListKeys = Object.keys(tabsList);
    const availableTabKeys: string[] = tabsListKeys.includes('guide') ? tabsListKeys : [...tabsListKeys, 'guide'];

    // Custom tabs first, then core tabs in DEFAULT_FOOTER_TABS_ORDER ... last guide
    const customTabKeys = availableTabKeys.filter((tabKey) => !DEFAULT_FOOTER_TABS_ORDER.includes(tabKey));
    const coreTabKeys = DEFAULT_FOOTER_TABS_ORDER.filter((tabKey) => availableTabKeys.includes(tabKey));
    const orderedTabKeys = customTabKeys.concat(coreTabKeys);

    return orderedTabKeys.map((tab, index) => {
      return {
        id: tab,
        value: index,
        label: allTabs[tab].label ? allTabs[tab].label : `${camelCase(tab)}.title`,
        icon: allTabs[tab]?.icon ?? '',
        content: <Box sx={sxClasses.tabContent}>{allTabs[tab]?.content ?? ''}</Box>,
      } as TypeTabs;
    });
  }, [memoTabs, tabsList, sxClasses]);

  /**
   * Handles adding a tab from the API.
   */
  const handleAddTab = useCallback((sender: FooterBarApi, event: FooterTabCreatedEvent): void => {
    const newTab = {
      [event.tab.id]: {
        icon: event.tab.icon || <InfoIcon />,
        label: event.tab.label,
        content: typeof event.tab.content === 'string' ? <UseHtmlToReact htmlContent={event.tab.content ?? ''} /> : event.tab.content,
      },
    } as Record<string, Tab>;

    // NOTE: we need prevState because of an async nature of adding plugins.
    setTabsList((prevState: Record<string, Tab>) => {
      return { ...prevState, ...newTab };
    });
  }, []);

  /**
   * Handles removing a tab from the API.
   */
  const handleRemoveTab = useCallback((sender: FooterBarApi, event: FooterTabRemovedEvent): void => {
    // remove the tab from the list
    setTabsList((prevState) => {
      const state = { ...prevState };
      delete state[event.tabid];
      return state;
    });
  }, []);

  /**
   * Updates the active footer tab based on footer tabs created from configuration.
   */
  useEffect(() => {
    if (!activeFooterBarTab.tabId && activeFooterBarTab.isOpen) setActiveFooterBarTab(memoFooterBarTabs?.[0]?.id ?? '');
    // No need to update when selected tab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoFooterBarTabs, setActiveFooterBarTab]);

  /**
   * Whenever the array layer data batch changes if we're on 'details' tab and it's collapsed, make sure we uncollapse it
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FOOTER-TABS - arrayOfLayerDataBatch', arrayOfLayerDataBatch, activeFooterBarTab);

    // If the details tab is not in the footer bar tabs config, return
    if (footerBarTabsConfig && !footerBarTabsConfig.tabs.core.includes('details')) return;

    // If we're on the details panel and the footer is collapsed
    if (activeFooterBarTab.tabId === 'details' && !activeFooterBarTab.isOpen) {
      // Uncollapse it
      setFooterBarIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrayOfLayerDataBatch, activeFooterBarTab, setFooterBarIsOpen]);
  // Don't add isCollapsed in the dependency array, because it'll retrigger the useEffect

  /**
   * Handle the collapse/expand state effect
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FOOTER-BAR - isFooterBarOpen', activeFooterBarTab.isOpen);

    if (tabsContainerRef.current) {
      const tabsContainer = tabsContainerRef.current;
      tabsContainer.style.height = 'fit-content';
      const lastChild = tabsContainer.firstElementChild?.lastElementChild as HTMLElement | null;
      if (lastChild) {
        lastChild.style.overflow = !activeFooterBarTab.isOpen ? 'unset' : '';
        lastChild.style.height = !activeFooterBarTab.isOpen ? '0px' : '';
      }
      tabsContainerRef.current = tabsContainer;
    }
  }, [activeFooterBarTab.isOpen, setActiveFooterBarTab]);

  /**
   * Handles the collapse/expand toggle.
   */
  const handleToggleCollapse = (): void => {
    if (activeFooterBarTab.isOpen) setActiveFooterBarTab(undefined);
    setFooterBarIsOpen(!activeFooterBarTab.isOpen);
  };

  /**
   * Handles when the selected tab changes.
   */
  const handleSelectedTabChanged = (tab: TypeTabs): void => {
    setActiveFooterBarTab(tab.id);
    setFooterBarIsOpen(true);
  };

  /**
   * Handles resizing the footerbar when toggling fullscreen.
   */
  useEffect(() => {
    if (!tabsContainerRef.current) {
      return;
    }

    logger.logTraceUseEffect('FOOTER-BAR - handle full screen resize', activeFooterBarTab.isOpen);

    // default values as set by the height of the div
    let footerHeight = 'fit-content';

    // adjust values from px to % to accomodate fullscreen plus page zoom
    if (isMapFullScreen && activeFooterBarTab.isOpen) {
      footerHeight = `${footerPanelResizeValue}%`;
    }

    tabsContainerRef.current.style.height = footerHeight;
  }, [tabsContainerRef, activeFooterBarTab.isOpen, isMapFullScreen, footerPanelResizeValue]);

  /**
   * Selects the active plugin when the tab changes.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FOOTER-BAR - selectedTab', activeFooterBarTab);

    // If clicked on a tab with a plugin
    if (activeFooterBarTab.tabId) {
      // Get the plugin if it exists
      MapEventProcessor.getMapViewerPluginIfExists(mapId, activeFooterBarTab.tabId)
        .then((plugin) => {
          // If the Plugin is a FooterPlugin
          if (plugin instanceof FooterPlugin) {
            // Select it
            plugin.select();
          }
        })
        .catch((error: unknown) => {
          // Log
          logger.logPromiseFailed('getMapViewerPlugin in useEffect in footer-bar', error);
        });
    }
  }, [mapId, activeFooterBarTab]);

  /**
   * Registers and unregisters tab create/remove event handlers on mount.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FOOTER-BAR - mount');

    // TODO: Refactor - Registering those handlers only when the component has been mounted can cause issues if 'someone' calls
    // TO.DOCONT: mapViewer('map1').footerBarApi.createTab() before the component is actually mounted.
    // TO.DOCONT: The handler when tabs are created should happen earlier and the ui component should only 'react' to store tabs values.
    // TO.DOCONT: We had an issue where a custom footer tab was never appearing, because the 'createTab()' call was happening before
    // TO.DOCONT: this FooterBar component was actually mounted. That being said, removing the 'mapLoaded' condition, before mounting
    // TO.DOCONT: the FooterBar (in Footer-bar.tsx), was sufficient to fix the issue for now, but it should be addressed eventually.

    // Register footerbar tab created/removed handlers
    footerBarApi.onFooterTabCreated(handleAddTab);
    footerBarApi.onFooterTabRemoved(handleRemoveTab);

    return () => {
      // Unregister events
      footerBarApi.offFooterTabCreated(handleAddTab);
      footerBarApi.offFooterTabRemoved(handleRemoveTab);
    };
  }, [footerBarApi, handleAddTab, handleRemoveTab]);

  /**
   * Creates default tabs from configuration parameters (similar logic as iin app-bar).
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FOOTER-BAR - footerBarTabsConfig');

    // TODO: Refactor Footer-bar - This loadScript shouldn't be part of a useEffect, because those happen everytime their depedencies change.
    // TO.DOCONT: In development with StrictMode this is triggered twice to prevent developers from doing stuff like that here.
    // TO.DOCONT: Also, important, the dependencies of this useEffect (and the code herein) isn't meant to be part of a useEffect in the first place.
    // TO.DOCONT: Looking at the other useEffects, it's clear there's a lot of refactoring that should be done in the Footer-bar in general.

    // Packages tab
    if (footerBarTabsConfig && footerBarTabsConfig.tabs.core.includes('time-slider')) {
      // Load and add the plugin
      MapEventProcessor.loadAndAddPlugin(mapId, 'time-slider').catch((error: unknown) => {
        // Log
        logger.logPromiseFailed('loadAndAddPlugin(time-slider) in useEffect in FooterBar', error);
      });
    }

    if (footerBarTabsConfig && footerBarTabsConfig.tabs.core.includes('geochart')) {
      // Load and add the plugin
      MapEventProcessor.loadAndAddPlugin(mapId, 'geochart').catch((error: unknown) => {
        // Log
        logger.logPromiseFailed('loadAndAddPlugin(geochart) in useEffect in FooterBar', error);
      });
    }
  }, [footerBarTabsConfig, mapId]);

  /**
   * Scrolls the footer into view on mouse click.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FOOTER BAR - scrollIntoViewListener');

    if (!tabsContainerRef?.current) return () => {};

    const handleClick = (): void => {
      // Need delay to allow tabs container to resize on expand/collapse
      delay(25).then(
        () => {
          if (tabsContainerRef.current) {
            scrollIfNotVisible(tabsContainerRef.current, 'end');
          }
        },
        (error: unknown) => logger.logPromiseFailed('Delay failed', error)
      );
    };

    const header = tabsContainerRef.current.querySelector('#footerbar-header');
    header?.addEventListener('click', handleClick);

    // Cleanup function to remove event listener
    return () => {
      header?.removeEventListener('click', handleClick);
    };
  }, []);

  return memoFooterBarTabs.length > 0 ? (
    <Box
      ref={tabsContainerRef as MutableRefObject<HTMLDivElement>}
      sx={sxClasses.tabsContainer}
      className="tabsContainer"
      id={`${mapId}-tabsContainer`}
    >
      <Tabs
        shellContainer={shellContainer}
        activeTrap={activeTrapGeoView}
        isCollapsed={!activeFooterBarTab.isOpen}
        onToggleCollapse={handleToggleCollapse}
        onSelectedTabChanged={handleSelectedTabChanged}
        onOpenKeyboard={enableFocusTrap}
        onCloseKeyboard={disableFocusTrap}
        selectedTab={memoFooterBarTabs.findIndex((t) => t.id === activeFooterBarTab.tabId)}
        tabProps={{ disableRipple: true }}
        tabs={memoFooterBarTabs}
        TabContentVisibilty={activeFooterBarTab.isOpen ? 'visible' : 'hidden'}
        containerType={CONTAINER_TYPE.FOOTER_BAR}
        rightButtons={activeFooterBarTab.isOpen && isMapFullScreen && <ResizeFooterPanel />}
        appHeight={appHeight}
        hiddenTabs={hiddenTabs}
        isFullScreen={isMapFullScreen}
      />
    </Box>
  ) : null;
}
